import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Row, Col, Dropdown, DatePicker,
  Typography, Button, Spin, Tabs, Badge, Divider, Alert, Tag
} from 'antd';
import {
  DashboardOutlined, BarChartOutlined, SettingOutlined, EnvironmentOutlined,
  WifiOutlined, DownOutlined, DownloadOutlined, ReloadOutlined, ApiOutlined
} from '@ant-design/icons';
import { MapContainer, TileLayer, CircleMarker, Popup, LayersControl, useMap } from 'react-leaflet';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';
import { useStore } from '../../stores/stores';
import SynfieldImage from '../../assets/Synfield.jpg';
import { getMeasurementIcon } from '../../utils/deviceUtils';

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;
const { BaseLayer } = LayersControl;

const chartColors = ['#1890ff', '#52c41a', '#fa8c16', '#722ed1', '#eb2f96'];

const deviceLocations = {
  'Dexameni':    [36.7850, 22.4292],
  'South Node':  [37.33796119565291, 21.780084153963788],
  'PV-House-HN': [37.277697391487656, 22.15739187360738],
  'drone':       [38.027069, 23.732949]
};

const CARD_COLOR = { bg: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)', shadow: 'rgba(24,144,255,0.25)' };

const RecenterMap = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center);
  }, [center, map]);
  return null;
};

const Devices = () => {
  const { deviceStore } = useStore();
  const [menuKey, setMenuKey] = useState('dashboard');
  const [datesSel, setDatesSel] = useState(false);

  const isDrone = deviceStore.selectedDevice === 'drone';

  useEffect(() => {
    deviceStore.fetchDevices().then(() => {
      if (!deviceStore.selectedDevice && deviceStore.devices.length > 0) {
        deviceStore.setSelectedDevice(deviceStore.devices[0].key);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedDevice = deviceStore.selectedDevice;
  useEffect(() => {
    deviceStore.fetchDeviceData();
    const interval = setInterval(() => deviceStore.fetchDeviceData(), 120000);
    return () => clearInterval(interval);
  }, [selectedDevice]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDevice = key => {
    deviceStore.setSelectedDevice(key);
    setMenuKey('dashboard');
    setDatesSel(false);
  };

  const handleMenu = key => {
    setMenuKey(key);
    deviceStore.setSelectedMeasurement(null);
    setDatesSel(false);
  };

  const handleMeas = ({ key }) => deviceStore.setSelectedMeasurement(key);

  const handleDateChange = dates => {
    deviceStore.setDateRange(dates);
    setDatesSel(false);
  };

  const handleFetchSensor = () => {
    const [start, end] = deviceStore.dateRange;
    if (deviceStore.selectedMeasurement && start && end) {
      setDatesSel(true);
      deviceStore.fetchSensorData(deviceStore.selectedMeasurement, start, end);
    }
  };

  const measurementMenuItems = {
    items: (isDrone ? deviceStore.measurements.drone : deviceStore.availableMeasurements).map(m => ({
      key: m.key,
      icon: getMeasurementIcon(m.key),
      label: m.title,
    })),
    onClick: handleMeas,
  };

  const renderDashboardCards = (items) => (
    <Row gutter={[20, 20]}>
      {items?.map((m) => (
          <Col key={m.title} xs={24} sm={12} md={8} xl={6}>
            <div style={{
              background: CARD_COLOR.bg,
              borderRadius: 14,
              padding: '20px 24px',
              boxShadow: `0 8px 24px ${CARD_COLOR.shadow}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              height: 110,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {m.title}
                </div>
                <div style={{ color: '#fff', fontSize: 24, fontWeight: 700, lineHeight: 1 }}>
                  {m.value}
                </div>
              </div>
              <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 32, marginLeft: 12, flexShrink: 0 }}>
                {m.icon}
              </div>
            </div>
          </Col>
        ))}

    </Row>
  );

  const renderSensorChart = () => {
    const data = deviceStore.sensorData;
    if (!Array.isArray(data) || data.length === 0)
      return <Alert message="No sensor data available for the selected range." type="info" showIcon style={{ marginTop: 20, borderRadius: 10 }} />;

    const dataKeys = Object.keys(data[0]).filter(k => k !== 'date');

    // Parse ISO strings directly so timezone is correct
    const chartData = data.map(d => {
      const ts = new Date(d.date).getTime();
      return { ...d, ts };
    });

    const timestamps = chartData.map(d => d.ts);
    const domainMin = Math.min(...timestamps);
    const domainMax = Math.max(...timestamps);

    const pad = n => String(n).padStart(2, '0');
    const fmtTs = (ts) => {
      const d = new Date(ts);
      return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth()+1)} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
    };
    const formatTick = fmtTs;
    const formatTooltipLabel = (ts) => {
      const d = new Date(ts);
      return `${d.getUTCFullYear()}-${pad(d.getUTCMonth()+1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} UTC`;
    };

    return (
      <div style={{ marginTop: 24 }}>
        <div style={{ marginBottom: 12, display: 'flex', gap: 10 }}>
          <Button icon={<DownloadOutlined />} onClick={() => deviceStore.exportSensorDataAsCSV()}>CSV</Button>
          <Button icon={<DownloadOutlined />} onClick={() => deviceStore.exportChartAsPNG()}>PNG</Button>
        </div>
        <div style={{
          background: '#fff',
          borderRadius: 14,
          padding: '24px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
        }}>
          <div id="chart-container" style={{ height: 360 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="ts"
                  type="number"
                  scale="time"
                  domain={[domainMin - 60000, domainMax + 60000]}
                  tickFormatter={formatTick}
                  tick={{ fontSize: 11, fill: '#888' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  domain={['auto', 'auto']}
                  tick={{ fontSize: 12, fill: '#888' }}
                  axisLine={false}
                  tickLine={false}
                  label={{
                    value: deviceStore.getUnitForSelectedMeasurement(),
                    angle: -90,
                    position: 'insideLeft',
                    style: { textAnchor: 'middle', fontSize: 12, fill: '#aaa' }
                  }}
                />
                <Tooltip
                  labelFormatter={formatTooltipLabel}
                  contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}
                />
                <Legend wrapperStyle={{ paddingTop: 12 }} />
                {dataKeys.map((key, i) => (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={chartColors[i % chartColors.length]}
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={{ r: 5 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <Divider style={{ margin: '16px 0 8px' }} />
          <div style={{ display: 'flex', gap: 20, color: '#888', fontSize: 13 }}>
            <span><Text strong style={{ color: '#555' }}>Unit:</Text> {deviceStore.getUnitForSelectedMeasurement()}</span>
            <span>
              <Text strong style={{ color: '#555' }}>Range:</Text>{' '}
              {deviceStore.dateRange?.[0]?.format('DD MMM YYYY')} → {deviceStore.dateRange?.[1]?.format('DD MMM YYYY')}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderMap = () => {
    const key = deviceStore.selectedDevice;
    const center = deviceLocations[key] || [38.0215, 23.7456];
    return (
      <div style={{ borderRadius: 14, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <MapContainer key={key} center={center} zoom={10} style={{ height: 480, width: '100%' }}>
          <RecenterMap center={center} />
          <LayersControl position="topright">
            <BaseLayer checked name="Map">
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            </BaseLayer>
            <BaseLayer name="Satellite">
              <TileLayer url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}" attribution="&copy; Google" />
            </BaseLayer>
          </LayersControl>
          <CircleMarker
            center={center}
            radius={12}
            pathOptions={{ color: '#1890ff', fillColor: '#1890ff', fillOpacity: 1, weight: 3 }}
          >
            <Popup><b>{key === 'drone' ? 'Synelixis Solutions S.A.' : key}</b></Popup>
          </CircleMarker>
        </MapContainer>
      </div>
    );
  };

  const droneEntry = { name: 'Drone', icon: <ApiOutlined />, key: 'drone' };
  const synfieldEntries = deviceStore.devices.map(d => ({
    name: d.name,
    key: d.key,
    icon: <img src={SynfieldImage} alt={d.name} style={{ width: 20, height: 20, borderRadius: 4, objectFit: 'cover' }} />
  }));
  const devices = [droneEntry, ...synfieldEntries];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f0f2f5', overflow: 'hidden' }}>

      {/* Sidebar */}
      <div style={{
        width: 230,
        background: 'linear-gradient(180deg, #001529 0%, #002140 100%)',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        padding: '28px 14px',
      }}>
        <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 16, paddingLeft: 10 }}>
          Devices
        </div>
        {devices.map(d => {
          const isSelected = deviceStore.selectedDevice === d.key;
          return (
            <div
              key={d.key}
              onClick={() => handleDevice(d.key)}
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                marginBottom: 4,
                borderRadius: 8,
                background: isSelected ? 'rgba(24,144,255,0.2)' : 'transparent',
                borderLeft: isSelected ? '3px solid #1890ff' : '3px solid transparent',
                color: isSelected ? '#fff' : 'rgba(255,255,255,0.55)',
                fontWeight: isSelected ? 600 : 400,
                fontSize: 14,
                transition: 'all 0.2s',
              }}
            >
              <span style={{ fontSize: 16, color: isSelected ? '#1890ff' : 'rgba(255,255,255,0.4)' }}>{d.icon}</span>
              <span style={{ flex: 1 }}>{d.name}</span>
              {isSelected && <Badge status="processing" />}
            </div>
          );
        })}
      </div>

      {/* Main content */}
      <div style={{ flex: 1, padding: 32, overflow: 'auto' }}>
        {!deviceStore.selectedDevice && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#bbb', gap: 16 }}>
            <WifiOutlined style={{ fontSize: 56 }} />
            <div style={{ fontSize: 16 }}>Select a device from the sidebar</div>
          </div>
        )}

        {deviceStore.selectedDevice && (
          <>
            {/* Device header */}
            <div style={{
              background: '#fff',
              borderRadius: 14,
              padding: '20px 28px',
              marginBottom: 24,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            }}>
              <div>
                <Title level={4} style={{ margin: 0 }}>{deviceStore.selectedDevice}</Title>
                <div style={{ marginTop: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Tag color="success" style={{ borderRadius: 20, fontSize: 12 }}>
                    <Badge status="processing" /> Online
                  </Tag>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Auto-refresh every 2 min
                  </Text>
                </div>
              </div>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => deviceStore.fetchDeviceData()}
                loading={deviceStore.loading}
                style={{ borderRadius: 8 }}
              >
                Refresh
              </Button>
            </div>

            {deviceStore.loading && !deviceStore.deviceData.dashboard?.length && (
              <div style={{ textAlign: 'center', padding: 60 }}>
                <Spin size="large" />
              </div>
            )}

            {deviceStore.error && (
              <Alert message={deviceStore.error} type="error" showIcon style={{ marginBottom: 20, borderRadius: 10 }} />
            )}

            {/* Tabs */}
            <Tabs
              activeKey={menuKey}
              onChange={handleMenu}
              tabBarStyle={{ background: '#fff', borderRadius: 14, padding: '0 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 24 }}
              items={[
                {
                  key: 'dashboard',
                  label: <span><DashboardOutlined /> Dashboard</span>,
                  children: renderDashboardCards(deviceStore.deviceData.dashboard),
                },
                {
                  key: 'sensors',
                  label: <span><BarChartOutlined /> Sensors</span>,
                  children: (
                    <div style={{ background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <Dropdown menu={measurementMenuItems} arrow>
                          <Button type="primary" ghost style={{ borderRadius: 8 }}>
                            {deviceStore.selectedMeasurement
                              ? deviceStore.availableMeasurements.find(m => m.key === deviceStore.selectedMeasurement)?.title || deviceStore.selectedMeasurement
                              : 'Select Measurement'
                            } <DownOutlined />
                          </Button>
                        </Dropdown>
                        {deviceStore.selectedMeasurement && (
                          <>
                            <RangePicker onChange={handleDateChange} style={{ borderRadius: 8 }} />
                            <Button type="primary" onClick={handleFetchSensor} style={{ borderRadius: 8 }}>
                              Show Data
                            </Button>
                          </>
                        )}
                      </div>
                      {deviceStore.loading && <Spin style={{ marginTop: 24, display: 'block' }} />}
                      {datesSel && renderSensorChart()}
                    </div>
                  ),
                },
                {
                  key: 'system',
                  label: <span><SettingOutlined /> System</span>,
                  children: renderDashboardCards(deviceStore.deviceData.system),
                },
                {
                  key: 'location',
                  label: <span><EnvironmentOutlined /> Location</span>,
                  children: renderMap(),
                },
              ]}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default observer(Devices);
